'use client';

import * as React from 'react';
import ReactMarkdown from 'react-markdown';
import type { Components } from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';
import { cn } from '@/lib/utils';

/** Lets embedded HTML (e.g. Zoho job descriptions) render like the markdown editor preview; sanitize follows GitHub-style defaults. */
const rehypePlugins = [rehypeRaw, rehypeSanitize];

interface MarkdownProps {
  children: string;
  className?: string;
  /**
   * Whether to highlight keywords in the markdown content
   */
  keywords?: string[];
  /**
   * Whether to use compact styling (less margin/padding)
   */
  compact?: boolean;
  /**
   * Whether this component is being used inside ExpandableContent
   */
  insideExpandable?: boolean;
}

/**
 * A themed markdown component that renders markdown content with styling
 * based on the application's theme.
 */
export function Markdown({
  children,
  className,
  keywords = [],
  compact = false,
  insideExpandable = false,
}: MarkdownProps) {
  // Custom components for ReactMarkdown
  const components: Components = {
    // Headers - increased spacing above headers for better section separation
    h1: ({ ...props }) => <h1 {...props} className={`text-3xl font-bold text-foreground ${compact ? 'mt-6 mb-4' : 'mt-6 mb-4'}`} />,
    h2: ({ ...props }) => <h2 {...props} className={`text-2xl font-bold text-foreground ${compact ? 'mt-5 mb-3.5' : 'mt-5 mb-3'}`} />,
    h3: ({ ...props }) => <h3 {...props} className={`text-xl font-semibold text-foreground ${compact ? 'mt-4.5 mb-3' : 'mt-4 mb-2'}`} />,
    h4: ({ ...props }) => <h4 {...props} className={`text-lg font-semibold text-foreground ${compact ? 'mt-4 mb-2.5' : 'mt-3 mb-2'}`} />,
    h5: ({ ...props }) => <h5 {...props} className={`text-base font-bold text-foreground ${compact ? 'mt-3.5 mb-2' : 'mt-3 mb-1'}`} />,
    h6: ({ ...props }) => <h6 {...props} className={`text-sm font-semibold text-foreground ${compact ? 'mt-3.5 mb-2' : 'mt-3 mb-1'}`} />,
    
    // Paragraphs and text - balanced spacing for compact mode
    p: ({ ...props }) => <p {...props} className={`text-secondary ${compact ? 'mb-3' : 'mb-4'}`} />,
    strong: ({ ...props }) => <strong {...props} className="font-bold text-foreground" />,
    b: ({ ...props }) => <b {...props} className="font-bold text-foreground" />,
    em: ({ ...props }) => <em {...props} className="italic" />,
    
    // Lists - balanced spacing for compact mode
    ul: ({ ...props }) => <ul {...props} className={`list-disc pl-6 text-secondary ${compact ? 'mb-3' : 'mb-4'}`} />,
    ol: ({ ...props }) => <ol {...props} className={`list-decimal pl-6 text-secondary ${compact ? 'mb-3' : 'mb-4'}`} />,
    li: ({ ...props }) => <li {...props} className={compact ? 'mb-1' : 'mb-1'} />,
    
    // Links and images
    a: ({ ...props }) => (
      <a 
        {...props} 
        className="text-primary-dark underline hover:text-secondary transition-colors" 
        target="_blank" 
        rel="noopener noreferrer"
      />
    ),
    img: ({ ...props }) => (
      <img 
        {...props} 
        className={`max-w-full h-auto rounded-lg ${compact ? 'my-3' : 'my-4'}`}
        alt={props.alt || 'Image'} 
      />
    ),
    
    // Code blocks - balanced spacing for compact mode
    code: ({ children, ...props }: React.HTMLProps<HTMLElement> & { inline?: boolean }) => {
      const inline = props.hasOwnProperty('inline');
      
      return inline ? (
        <code 
          {...props} 
          className="bg-card-background px-1.5 py-0.5 rounded text-sm font-mono"
        >
          {children}
        </code>
      ) : (
        <code 
          {...props} 
          className={`block bg-card-background p-4 rounded-lg overflow-x-auto text-sm font-mono ${compact ? 'my-3' : 'my-4'}`}
        >
          {children}
        </code>
      );
    },
    pre: ({ ...props }) => <pre {...props} className={`bg-card-background p-4 rounded-lg overflow-x-auto ${compact ? 'my-3' : 'my-4'}`} />,
    
    // Blockquotes - balanced spacing for compact mode
    blockquote: ({ ...props }) => (
      <blockquote 
        {...props} 
        className={`border-l-4 border-secondary pl-4 italic text-secondary ${compact ? 'my-3' : 'my-4'}`}
      />
    ),
    
    // Tables - balanced spacing for compact mode
    table: ({ ...props }) => <table {...props} className={`w-full border-collapse ${compact ? 'my-3' : 'my-4'}`} />,
    thead: ({ ...props }) => <thead {...props} className="bg-card-background" />,
    tbody: ({ ...props }) => <tbody {...props} />,
    tr: ({ ...props }) => <tr {...props} className="border-b border-secondary" />,
    th: ({ ...props }) => <th {...props} className="py-2 px-4 text-left font-bold text-foreground" />,
    td: ({ ...props }) => <td {...props} className="py-2 px-4 text-secondary" />,
    
    // Horizontal rule - balanced spacing for compact mode
    hr: ({ ...props }) => <hr {...props} className={`border-t border-secondary ${compact ? 'my-4' : 'my-6'}`} />,

    // Raw HTML from imports (Zoho, etc.) — keep structure; avoid extra margins on every div
    div: ({ ...props }) => <div {...props} className="text-secondary" />,
    span: ({ ...props }) => <span {...props} className="text-secondary" />,
  };

  // Function to highlight keywords in text nodes
  const textRenderer = React.useCallback((text: string) => {
    if (!keywords.length) return text;
    
    const parts: React.ReactNode[] = [];
    const pattern = new RegExp(`(${keywords.join('|')})`, 'gi');
    let lastIndex = 0;
    let match;
    
    while ((match = pattern.exec(text)) !== null) {
      if (match.index > lastIndex) {
        parts.push(text.slice(lastIndex, match.index));
      }
      
      parts.push(
        <mark key={match.index} className="bg-accent rounded px-1">
          {match[0]}
        </mark>
      );
      
      lastIndex = pattern.lastIndex;
    }
    
    if (lastIndex < text.length) {
      parts.push(text.slice(lastIndex));
    }
    
    return parts.length ? <>{parts}</> : text;
  }, [keywords]);

  // Add text renderer to components if keywords are provided
  if (keywords.length > 0) {
    components.text = ({ children }) => textRenderer(children as string);
  }

  return (
    <div className={cn(
      'markdown-content',
      !insideExpandable && (compact ? 'space-y-2' : 'space-y-4'),
      className
    )}>
      <ReactMarkdown rehypePlugins={rehypePlugins} components={components}>
        {typeof children === 'string' ? children : ''}
      </ReactMarkdown>
    </div>
  );
} 